import { useState } from 'react';
import { X, Settings, Globe, Volume2, Eye, BookOpen, Mail, CheckCircle2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useTutorial } from '@/hooks/useTutorial';
import { useAuth } from '@/hooks/useAuth';
import { useCompleteSocialTask } from '@/hooks/useSocialTasks';
import { useEmailVerificationStatus } from '@/hooks/useEmailVerification';
import { EmailVerificationDialog } from '@/components/EmailVerificationDialog';

interface SettingsSheetProps {
  open: boolean;
  onClose: () => void;
}

export function SettingsSheet({ open, onClose }: SettingsSheetProps) {
  const [settings, setSettings] = useState({
    language: 'en',
    sounds: true,
    animations: true,
  });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const { openTutorial } = useTutorial();
  useAuth();
  const { data: emailStatus, refetch: refetchEmailStatus } = useEmailVerificationStatus();
  const completeSocialTask = useCompleteSocialTask();

  // Check if email is verified from direct database query
  const emailVerified = emailStatus?.verified ?? false;
  const userEmail = emailStatus?.email ?? null;

  const updateSetting = <K extends keyof typeof settings>(
    key: K,
    value: (typeof settings)[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    // In a real app, you'd persist this to localStorage or user preferences
  };

  const handleViewTutorial = () => {
    console.log('[SettingsSheet] View Tutorial clicked');
    onClose();
    console.log('[SettingsSheet] Calling openTutorial()');
    openTutorial();
  };

  const handleEmailVerificationSuccess = async () => {
    // Complete the email social task to award 100 points
    try {
      await completeSocialTask.mutateAsync('email');
      // Refetch email verification status to update the button
      await refetchEmailStatus();
    } catch (error) {
      // Error already handled by the mutation hook
      console.error('Failed to complete email social task:', error);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 lg:inset-y-0 lg:right-0 lg:left-auto lg:w-80">
      {/* Backdrop (mobile only) */}
      <div className="lg:hidden absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Sheet */}
      <div
        className={cn(
          'absolute inset-x-0 bottom-0 lg:inset-y-0 lg:right-0',
          'border-t lg:border-t-0 lg:border-l',
          'rounded-t-xl lg:rounded-none',
          'shadow-2xl',
          'max-h-[80vh] lg:max-h-none lg:h-full',
          'animate-slide-in',
          'flex flex-col'
        )}
        style={{
          backgroundColor: '#343434',
          borderColor: '#50E5F8',
          boxShadow: '0 0 20px rgba(80, 228, 249, 0.3)',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4" style={{ borderBottom: '1px solid rgba(80, 228, 249, 0.3)' }}>
          <div className="flex items-center space-x-2">
            <Settings size={20} style={{ color: '#50E5F8' }} />
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>SETTINGS</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full transition-colors"
            style={{ color: 'white' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-4 space-y-6">
            {/* View Tutorial Button */}
            <button
              onClick={handleViewTutorial}
              className="w-full p-4 rounded-lg transition-all hover:scale-[1.02]"
              style={{
                backgroundColor: 'rgba(80, 228, 249, 0.1)',
                border: '2px solid #50E5F8',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(80, 228, 249, 0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(80, 228, 249, 0.1)'}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <BookOpen size={20} style={{ color: '#50E5F8' }} />
                  <div className="text-left">
                    <h3 className="font-medium" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                      VIEW TUTORIAL
                    </h3>
                    <p className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                      Learn how to use Junk.Fun
                    </p>
                  </div>
                </div>
                <div style={{ color: '#50E5F8', fontSize: '20px' }}>→</div>
              </div>
            </button>

            {/* Verify Email Button */}
            <button
              onClick={() => !emailVerified && setEmailDialogOpen(true)}
              disabled={emailVerified}
              className="w-full p-4 rounded-lg transition-all hover:scale-[1.02] disabled:hover:scale-100 disabled:cursor-default"
              style={{
                backgroundColor: emailVerified ? 'rgba(76, 175, 80, 0.1)' : 'rgba(80, 228, 249, 0.1)',
                border: emailVerified ? '2px solid #4CAF50' : '2px solid #50E5F8',
              }}
              onMouseEnter={(e) => {
                if (!emailVerified) {
                  e.currentTarget.style.backgroundColor = 'rgba(80, 228, 249, 0.2)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = emailVerified ? 'rgba(76, 175, 80, 0.1)' : 'rgba(80, 228, 249, 0.1)';
              }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {emailVerified ? (
                    <CheckCircle2 size={20} style={{ color: '#4CAF50' }} />
                  ) : (
                    <Mail size={20} style={{ color: '#50E5F8' }} />
                  )}
                  <div className="text-left">
                    <h3 className="font-medium" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>
                      {emailVerified ? 'EMAIL VERIFIED' : 'VERIFY EMAIL'}
                    </h3>
                    <p className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                      {emailVerified && userEmail ? userEmail : 'Link your email address'}
                    </p>
                  </div>
                </div>
                {!emailVerified && <div style={{ color: '#50E5F8', fontSize: '20px' }}>→</div>}
                {emailVerified && <CheckCircle2 size={24} style={{ color: '#4CAF50' }} />}
              </div>
            </button>

            {/* Language */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <Globe size={16} style={{ color: '#50E5F8' }} />
                <h3 className="font-medium" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>LANGUAGE</h3>
              </div>

              <div className="pl-6">
                <select
                  value={settings.language}
                  onChange={(e) => updateSetting('language', e.target.value)}
                  className="w-full p-2 rounded-md focus:ring-2 focus:ring-offset-0 outline-none"
                  style={{
                    border: '2px solid #50E5F8',
                    backgroundColor: '#262626',
                    color: 'white',
                    fontFamily: 'Outfit, sans-serif'
                  }}
                >
                  <option value="en" style={{ backgroundColor: '#262626', color: 'white' }}>English</option>
                  {/* Add more languages as needed */}
                </select>
              </div>
            </div>

            {/* Sound Effects */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Volume2 size={16} style={{ color: '#50E5F8' }} />
                  <h3 className="font-medium" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>SOUND EFFECTS</h3>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.sounds}
                    onChange={(e) => updateSetting('sounds', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div
                    className={cn(
                      'w-11 h-6 rounded-full peer',
                      'peer-focus:ring-2',
                      "after:content-[''] after:absolute after:top-[2px] after:left-[2px]",
                      'after:bg-white after:rounded-full after:h-5 after:w-5',
                      'after:transition-all peer-checked:after:translate-x-full'
                    )}
                    style={{
                      backgroundColor: settings.sounds ? '#50E5F8' : 'rgba(255, 255, 255, 0.2)'
                    }}
                  />
                </label>
              </div>

              <div className="pl-6 text-sm" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                Enable sound effects for dumps and rewards
              </div>
            </div>

            {/* Animations */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Eye size={16} style={{ color: '#50E5F8' }} />
                  <h3 className="font-medium" style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}>ANIMATIONS</h3>
                </div>

                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.animations}
                    onChange={(e) => updateSetting('animations', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div
                    className={cn(
                      'w-11 h-6 rounded-full peer',
                      'peer-focus:ring-2',
                      "after:content-[''] after:absolute after:top-[2px] after:left-[2px]",
                      'after:bg-white after:rounded-full after:h-5 after:w-5',
                      'after:transition-all peer-checked:after:translate-x-full'
                    )}
                    style={{
                      backgroundColor: settings.animations ? '#50E5F8' : 'rgba(255, 255, 255, 0.2)'
                    }}
                  />
                </label>
              </div>

              <div className="pl-6 text-sm" style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.7)' }}>
                Enable visual animations and effects
              </div>
            </div>
          </div>

          {/* App Info */}
          <div className="p-4" style={{ borderTop: '1px solid rgba(80, 228, 249, 0.3)', backgroundColor: 'rgba(80, 228, 249, 0.1)' }}>
            <div className="space-y-2 text-sm" style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}>
              <div className="flex justify-between">
                <span>Version</span>
                <span>1.0.1</span>
              </div>

              <div className="flex justify-between">
                <span>Build</span>
                <span>Production</span>
              </div>

              <div className="flex justify-between">
                <span>Network</span>
                <span>Solana Mainnet Beta</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Verification Dialog */}
      <EmailVerificationDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        onVerificationSuccess={handleEmailVerificationSuccess}
      />
    </div>
  );
}
